/*
 * Copyright (c) 2023. Word Wide Generation Ltd
 */

import { useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import { SubmitButton } from '@components/button/SubmitButton';
import { RootInitiativeData } from '../../../../types/initiative';
import { BasicContainer, CounterWidget } from '@g17eco/atoms';
import IconButton from '../../../../components/button/IconButton';
import { usePreviewPlanMutation, useUpgradePlanMutation } from '../../../../api/initiatives';
import { useSiteAlert } from '../../../../hooks/useSiteAlert';
import { useAppDispatch } from '../../../../reducers';
import { QueryError } from '@components/query/QueryError';
import { CanUpgradeDetails } from '../../../../types/subscriptions';
import { FeatureCode } from '@g17eco/core';
import { reloadInitiativeTree } from '../../../../actions';
import { useDebouncedCallback } from 'use-debounce';
import LoadingPlaceholder from '../../../../components/LoaderContainer/LoadingPlaceholder';
import { getPreviewDetails, InvoiceDetails } from '../payment-details/InvoicePreview';


export interface UpgradeFlowProps {
  rootOrg: RootInitiativeData;
  onSuccess: () => void;
  onCancel: () => void;
  minSeats?: number;
  maxSeats?: number;
  upgradeDetails: CanUpgradeDetails;
  defaultUnits?: number;
}

export function UpgradeFlow(props: Readonly<UpgradeFlowProps>) {

  const {
    rootOrg,
    onSuccess,
    onCancel,
    upgradeDetails,
    minSeats = 1,
    maxSeats = 100,
    defaultUnits = 1,
  } = props;

  const [seats, setSeats] = useState(defaultUnits);

  const [onUpgrade, { isError, error }] = useUpgradePlanMutation();
  const [onPreview, previewResponse] = usePreviewPlanMutation();
  const { addSiteAlert } = useSiteAlert();
  const dispatch = useAppDispatch();

  const previewInvoice = useDebouncedCallback((units: number) => {
    onPreview({
      additionalUnits: units,
      initiativeId: rootOrg._id,
      featureCode: FeatureCode.Users
    })
  }, 1000);

  useEffect(() => {
    // Load things initially
    onPreview({
      additionalUnits: defaultUnits,
      initiativeId: rootOrg._id,
      featureCode: FeatureCode.Users
    })
  }, [defaultUnits, onPreview, rootOrg._id])


  const handleClick = async () => {
    await onUpgrade({
      additionalUnits: seats,
      initiativeId: rootOrg._id,
      featureCode: FeatureCode.Users
    }).unwrap().then(() => {
      dispatch(reloadInitiativeTree())
      addSiteAlert({
        content: `Success. Additional ${seats} seats were added`
      })
      onSuccess();
    }).catch(() => {
    })
  };



  const onChangeUnit = (changeUnit: number) => {
    const units = seats + changeUnit;
    setSeats(units)
    previewInvoice(units)
  }

  const { price, currencySymbol } = upgradeDetails.productDetails;

  const isValid = seats > 0;
  const extraCost = seats * price;

  const { currentUsage, limit } = upgradeDetails.usageDetails
  const { total, dueDate } = getPreviewDetails(previewResponse);

  return (
    <div className={'upgrade-flow-container'}>
      {isError && error ? <QueryError error={error} type={'danger'} /> : null}
      <h3>Add seats</h3>

      <div className={'mb-3 d-flex align-items-center justify-content-between'}>
        <div>
          You are using {currentUsage}/{limit} user seats in your subscription. <br />
          You can add more seats here.
        </div>
        <div className={'d-flex align-items-center'}>
          <IconButton
            color={'primary'}
            icon={'fa-minus'}
            disabled={seats <= minSeats}
            onClick={() => onChangeUnit(-1)} />

          <CounterWidget count={seats} />

          <IconButton
            color={'primary'}
            icon={'fa-plus'}
            outline={false}
            disabled={seats >= maxSeats}
            onClick={() => onChangeUnit(1)} />
        </div>
      </div>


      <BasicContainer size={'lg'} mb={3} appearance={'info'}>
        <div className={'d-flex justify-content-between'}>
          <h5>Summary</h5>
          <h5>Cost per month</h5>
        </div>
        <div className={'d-flex justify-content-between'}>
          <p className={'flex-grow-1'}>You are adding {seats} extra seats today</p>
          <p className={'text-lg'}>+{currencySymbol}{extraCost}</p>
        </div>

        <LoadingPlaceholder isLoading={previewResponse.isLoading || previewResponse.isUninitialized} height={40}>
          <div className={'d-flex justify-content-between'}>
            <span className={'flex-grow-1'}>Total due {dueDate}</span>
            <span className={'text-lg'}>{currencySymbol}{total}</span>
          </div>
          <div className={'text-xs text-right'}>
            tax included in price
          </div>
          <InvoiceDetails previewResponse={previewResponse} />
        </LoadingPlaceholder>
      </BasicContainer>


      <div className={'d-flex align-items-center justify-content-between'}>
        <div className={'text-xs'}>
          The amount is prorated this month, you will be charged the full amount at the beginning of your next billing
          cycle.
        </div>
        <div className='d-flex flex-shrink-1'>
          <Button className={'mr-3'} color='link-secondary' onClick={onCancel}>
            Cancel
          </Button>
          <SubmitButton color={'primary'} disabled={!isValid} onClick={handleClick}>
            Continue
          </SubmitButton>
        </div>
      </div>
    </div>
  );
}
